import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo } from 'react';
import { useAsyncFn, useMount } from 'react-use-v13';

import { SpeedAuditVersion } from '@scale/shared/defaults';
import Spinner from 'design-system/Spinner';
import { useParams, useLocation, useHistory } from 'react-router-dom';

import { useBeginAudit } from 'react-scale/components/ExpertManagement/hooks/audit/useBeginAudit';
import { useSpeedAuditResultsMap } from 'react-scale/components/ExpertManagement/hooks/audit/useSpeedAuditResultsMap';
import { useFetchAuditFromBatchId } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchAuditFromBatchId';
import { useFetchLoggedInUser } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchLoggedInUser';
import {
  useAuditActions,
  useLoadedAudit,
  useLoadedSubtasks,
  useOpenChatBulkAuditResults,
} from 'react-scale/components/ExpertManagement/stores/audit.store';
import SpeedAuditDialog from 'react-scale/components/audits/SpeedAudit/SpeedAuditDialog';
import { AuditBatch, SpeedAuditLookup } from 'react-scale/components/audits/SpeedAudit/types';
import ChatBulkAuditPage from '../../../corp/views/chatBulkAudit/ChatBulkAuditPage';
import ChatBulkAuditResults from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditResults';
import qs from 'qs';

export const SpeedAuditModal: React.FC = () => {
  const audit = useLoadedAudit();
  const subtaskAssignments = useLoadedSubtasks();
  const attemptAudits = useMemo(() => (audit?.attemptAudits ?? []) as any, [audit?.attemptAudits]);
  const { speedAuditResults, speedAuditResultsActions } = useSpeedAuditResultsMap();
  const { reset } = useAuditActions();
  const { beginAudit } = useBeginAudit();
  const { data: user } = useFetchLoggedInUser();
  const openChatBulkAuditResults = useOpenChatBulkAuditResults();
  const location = useLocation();
  const history = useHistory();
  const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const auditBatchIdForChatBulkAuditFromUrl = searchParams.auditBatchIdChatBulkAudit as string;

  const objectIds = useMemo(
    () => audit?.attemptAudits.map(a => a._id) ?? [],
    [audit?.attemptAudits],
  );
  const { mutate } = useFetchAuditFromBatchId();

  const query = useParams<{ batchId?: string; name?: string; project?: string }>();
  const defaultAuditBatchId: string | undefined =
    (query?.batchId as string | undefined) ?? undefined;

  const [index, setIndex] = React.useState<number>(0);
  const currentAttemptAudit = useMemo(
    () => attemptAudits[index] as SpeedAuditLookup | undefined,
    [index, attemptAudits],
  );

  const getNextUnauditedObject = useCallback(
    (attemptAudits: SpeedAuditLookup[], startIndex: number) => {
      for (let i = startIndex + 1; i < attemptAudits.length; i++) {
        const objectLookup = attemptAudits[i];

        const isNotAudited =
          !objectLookup.resultV2?.length &&
          !speedAuditResults[objectLookup.auditLookup.id]?.resultV2?.length;

        if (isNotAudited) return { audit: objectLookup, index: i };
      }

      const lastIndex = attemptAudits.length - 1;
      return { audit: attemptAudits[lastIndex], index: lastIndex };
    },
    [speedAuditResults],
  );

  const getPrevUnauditedObject = useCallback(
    (attemptAudits: SpeedAuditLookup[], startIndex: number) => {
      for (let i = startIndex - 1; i > 0; i--) {
        const objectLookup = attemptAudits[i];
        if (!objectLookup.result && !speedAuditResults[objectLookup.auditLookup.id]?.result)
          return { audit: objectLookup, index: i };
      }

      return { audit: attemptAudits[0], index: 0 };
    },
    [speedAuditResults],
  );

  const updateIndex = useCallback(
    (indexOrCallback: number | ((prev: number) => number)) => {
      setIndex(prev => {
        const newIndex =
          typeof indexOrCallback === 'function' ? indexOrCallback(prev) : indexOrCallback;
        const boundedIndex = Math.max(Math.min(newIndex, attemptAudits.length - 1), 0);
        return boundedIndex;
      });
    },
    [attemptAudits],
  );

  const nextIndex = useCallback(() => {
    updateIndex(prev => prev + 1);
  }, [updateIndex]);

  const prevIndex = useCallback(() => {
    updateIndex(prev => prev - 1);
  }, [updateIndex]);

  const nextUnauditedIndex = useCallback(() => {
    updateIndex(prev => getNextUnauditedObject(attemptAudits, prev).index);
  }, [attemptAudits, getNextUnauditedObject, updateIndex]);

  const prevUnauditedIndex = useCallback(() => {
    updateIndex(prev => getPrevUnauditedObject(attemptAudits, prev).index);
  }, [attemptAudits, getPrevUnauditedObject, updateIndex]);

  useMount(() => {
    // Load an existing Batch (if any) from the URL Query Parameter
    if (defaultAuditBatchId) {
      mutate({ batchId: defaultAuditBatchId, projectId });
    }
  });

  const resetAuditBatch = useCallback(() => {
    speedAuditResultsActions.reset();
    setIndex(0);
    reset();
  }, [reset, speedAuditResultsActions]);

  const [updateAuditBatch, handleUpdateAuditBatch] = useAsyncFn(
    async (auditBatch?: any, _attemptIds: string[] = []) => {
      resetAuditBatch();

      if (!auditBatch) return;

      beginAudit({ projectId: auditBatch.projectId, batchId: auditBatch._id });
    },
  );

  const handleComplete = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.has('auditBatchIdChatBulkAudit')) {
      queryParams.delete('auditBatchIdChatBulkAudit');
      history.replace({
        search: queryParams.toString(),
      });
      // the nice thing about window.close is that it will only close the tab if it was programmatically opened
      // so if the user opened the tab themselves, it won't close, but if we open it (it'll clean up after itself)
      window.close();
    }

    resetAuditBatch();
  }, [resetAuditBatch, location, history]);

  if (updateAuditBatch.loading) {
    return (
      <Typography variant="h6">
        Fetching new audits with updated thresholds/manually excluded reviewers...
        <Spinner center size="md" />
      </Typography>
    );
  }

  const projectInstrItems = audit?.projectInstrItems;
  const auditBatch = audit?.auditBatch;
  const projectId = audit?.projectId ?? '';

  return (
    <div>
      {projectInstrItems && auditBatch && (
        <SpeedAuditDialog
          projectInstrItems={projectInstrItems}
          projectId={projectId}
          objectIds={objectIds}
          auditBatch={auditBatch as AuditBatch}
          onRequestClose={handleComplete}
          onUpdateAuditBatch={handleUpdateAuditBatch}
          subtaskAssignments={subtaskAssignments}
          speedAuditResults={speedAuditResults}
          speedAuditResultsActions={speedAuditResultsActions}
          currentAttemptAudit={currentAttemptAudit}
          index={index}
          setIndex={updateIndex}
          nextIndex={nextIndex}
          prevIndex={prevIndex}
          nextUnauditedIndex={nextUnauditedIndex}
          prevUnauditedIndex={prevUnauditedIndex}
          speedAuditVersion={SpeedAuditVersion.v2}
          user={user ?? ({} as any)}
        />
      )}
      {auditBatchIdForChatBulkAuditFromUrl && !openChatBulkAuditResults && (
        <ChatBulkAuditPage
          auditBatchIdOverride={auditBatchIdForChatBulkAuditFromUrl}
          user={user ?? ({} as any)}
        />
      )}
      {auditBatchIdForChatBulkAuditFromUrl && openChatBulkAuditResults && (
        <ChatBulkAuditResults
          auditBatchId={auditBatchIdForChatBulkAuditFromUrl}
          user={user ?? ({} as any)}
          handleComplete={handleComplete}
        />
      )}
    </div>
  );
};
